'use client';

import { c as _c } from "react/compiler-runtime";
import React, { type ReactElement, type Ref, useId, useRef } from 'react';
import mergeRefs from 'react-merge-refs';
export type RadioProps<T extends string = string> = {
  checked?: boolean;
  defaultChecked?: boolean;
  disabled?: boolean;
  hasError?: boolean;
  hint?: string;
  id?: string;
  label?: React.ReactNode;
  name?: string;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onChange?: (event: React.ChangeEvent<Omit<HTMLInputElement, 'value'> & {
    value: T;
  }>) => void;
  readOnly?: boolean;
  required?: boolean;
  testID?: string;
  value?: T;
};
const Radio = (t0, ref) => {
  const $ = _c(30);
  const {
    checked,
    defaultChecked,
    disabled,
    hasError,
    hint,
    id: possibleId,
    label,
    name,
    onBlur,
    onChange,
    readOnly,
    required,
    testID,
    value
  } = t0;
  const inputRef = useRef(null);
  const internalId = useId();
  const id = possibleId || internalId;
  const t1 = "text-body-2 text-primary inline-flex items-center gap-3 cursor-pointer data-[has-error=true]:text-error data-[disabled=true]:cursor-[initial]";
  const t2 = hint && `${id}-hint`;
  const t3 = "fp-radio";
  const t4 = mergeRefs([inputRef, ref]);
  const t5 = disabled ? undefined : 0;
  let t6;
  if ($[0] !== checked || $[1] !== defaultChecked || $[2] !== disabled || $[3] !== hasError || $[4] !== id || $[5] !== name || $[6] !== onBlur || $[7] !== onChange || $[8] !== readOnly || $[9] !== required || $[10] !== t4 || $[11] !== t5 || $[12] !== value) {
    t6 = <input className={t3} aria-checked={checked} aria-disabled={disabled} aria-required={required} checked={checked} data-has-error={hasError} defaultChecked={defaultChecked} disabled={disabled} id={id} name={name} onBlur={onBlur} onChange={onChange} readOnly={readOnly} ref={t4} required={required} tabIndex={t5} type="radio" value={value} />;
    $[0] = checked;
    $[1] = defaultChecked;
    $[2] = disabled;
    $[3] = hasError;
    $[4] = id;
    $[5] = name;
    $[6] = onBlur;
    $[7] = onChange;
    $[8] = readOnly;
    $[9] = required;
    $[10] = t4;
    $[11] = t5;
    $[12] = value;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== disabled || $[15] !== hasError || $[16] !== id || $[17] !== label || $[18] !== t2 || $[19] !== t6 || $[20] !== testID) {
    t7 = <label className={t1} aria-describedby={t2} data-testid={testID} data-has-error={hasError} htmlFor={id} data-disabled={disabled}>{t6}{label}</label>;
    $[14] = disabled;
    $[15] = hasError;
    $[16] = id;
    $[17] = label;
    $[18] = t2;
    $[19] = t6;
    $[20] = testID;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== hasError || $[23] !== hint || $[24] !== id || $[25] !== inputRef) {
    t8 = hint && <div className="fp-radio-hint text-body-2 text-left ml-[28px] mt-0.5 text-(--hint-color) data-[has-error=true]:text-(--hint-error-color)" data-has-error={hasError} id={`${id}-hint`} onClick={() => {
      inputRef.current?.click();
    }}>{hint}</div>;
    $[22] = hasError;
    $[23] = hint;
    $[24] = id;
    $[25] = inputRef;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  let t9;
  if ($[27] !== t7 || $[28] !== t8) {
    t9 = <div>{t7}{t8}</div>;
    $[27] = t7;
    $[28] = t8;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  return t9;
};
type ForwardRef = <T extends string = string>(p: RadioProps<T> & {
  ref?: Ref<HTMLDivElement>;
}) => ReactElement<any>;
export default React.forwardRef(Radio) as ForwardRef;